import ko from 'knockout';
import moment from 'moment';
import { openPopup, closePopups } from "../../init";
import { CustomError, customFetch as fetch } from "../../fetch";
import { errorToast, successToast } from "../../toastr-helper";
import SelectListItemModel from "../selectlist-item-model";

export enum EmployeeProfileMutationPopupModelType {
    Iban = 1,
    PayrollTaxDeduction = 2,
    Address = 4,
    HouseNumber = 5,
    City = 6,
    PostalCode = 7,
    Country = 8
}

export interface AddressMutationModel {
    address: string;
    houseNumber: string;
    postalCode: string;
    city: string;
    country: string;
}

export interface AddEmployeeProfileMutationRequest {
    employeeId: string;
    mutationStartDate: string;
    values?: Partial<Record<EmployeeProfileMutationPopupModelType, any>>;
}

export interface EmployeeProfileMutationPopupModelOptions {
    employeeId: string;
    type: EmployeeProfileMutationPopupModelType;
    payrollTaxDeductionEnabled?: boolean;
    pendingPayrollTaxDeductionEnabledStartDate: moment.Moment | null;
    pendingPayrollTaxDeductionEnabledValue: boolean;
    pendingIbanMutationValue: string;
    pendingIbanMutationStartDate: moment.Moment | null;
    pendingAddressDetails: AddressMutationModel;
    pendingAddressMutationStartDate: moment.Moment | null;
    countries: SelectListItemModel[];
    
    submitUrl: string;
    onSuccess?: () => void;
}

export class EmployeeProfileMutationPopupModel {
    validationErrors: KnockoutObservableArray<CustomError>;

    saving: KnockoutObservable<boolean>;
    mutationStartDate: KnockoutObservable<moment.Moment>;
    type: KnockoutObservable<EmployeeProfileMutationPopupModelType>;
    iban: KnockoutObservable<string>;
    address: KnockoutObservable<string>;
    houseNumber: KnockoutObservable<string>;
    postalCode: KnockoutObservable<string>;
    city: KnockoutObservable<string>;
    country: KnockoutObservable<number>;

    pendingFullAddress: KnockoutObservable<string>;
    pendingPayrollTaxDeductionEnabledStartDate: KnockoutObservable<moment.Moment | null>;
    pendingPayrollTaxDeductionEnabledValue: KnockoutObservable<boolean>;
    pendingIbanMutationValue: KnockoutObservable<string>;
    pendingIbanMutationStartDate: KnockoutObservable<moment.Moment | null>;
    payrollTaxDeductionEnabled: KnockoutObservable<boolean>;
    pendingAddressMutationStartDate: KnockoutObservable<moment.Moment | null>;
    countries: KnockoutObservableArray<SelectListItemModel>;

    pendingAddressMutationValue: KnockoutComputed<string>;

    onSuccess?: () => void;
    employeeId: string;
    submitUrl: string;

    constructor() {
        this.validationErrors = ko.observableArray<CustomError>();
        this.saving = ko.observable<boolean>();

        this.pendingIbanMutationStartDate = ko.observable<moment.Moment | null>();
        this.pendingPayrollTaxDeductionEnabledStartDate = ko.observable<moment.Moment | null>();
        this.pendingPayrollTaxDeductionEnabledValue = ko.observable<boolean>();
        this.pendingIbanMutationValue = ko.observable<string>();
        this.pendingAddressMutationStartDate = ko.observable<moment.Moment | null>();
        this.pendingFullAddress = ko.observable<string>();

        this.mutationStartDate = ko.observable<moment.Moment>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'MutationStartDate' }
            });
        this.type = ko.observable<EmployeeProfileMutationPopupModelType>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'Type' }
            });
        this.iban = ko.observable<string>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'Iban' }
            });
        this.address = ko.observable<string>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'Address' }
            });
        this.houseNumber = ko.observable<string>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'HouseNumber' }
            });
        this.country = ko.observable<number>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'Country' }
            })
        this.postalCode = ko.observable<string>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'PostalCode' }
            });
        this.city = ko.observable<string>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'City' }
            })
        this.payrollTaxDeductionEnabled = ko.observable<boolean>();
        this.countries = ko.observableArray<SelectListItemModel>();
    }

    init(options: EmployeeProfileMutationPopupModelOptions) {
        this.employeeId = options.employeeId;
        this.submitUrl = options.submitUrl;
        this.onSuccess = options.onSuccess;

        this.mutationStartDate(moment());
        this.type(options.type);
        this.payrollTaxDeductionEnabled(options.payrollTaxDeductionEnabled || false);
        this.pendingPayrollTaxDeductionEnabledStartDate(options.pendingPayrollTaxDeductionEnabledStartDate);
        this.pendingPayrollTaxDeductionEnabledValue(options.pendingPayrollTaxDeductionEnabledValue);
        this.pendingIbanMutationStartDate(options.pendingIbanMutationStartDate);
        this.pendingIbanMutationValue(options.pendingIbanMutationValue);
        this.pendingAddressMutationStartDate(options.pendingAddressMutationStartDate);
        this.address(options.pendingAddressDetails.address);
        this.houseNumber(options.pendingAddressDetails.houseNumber);
        this.postalCode(options.pendingAddressDetails.postalCode);
        this.city(options.pendingAddressDetails.city);
        this.countries(options.countries);

        const pendingFullAddress = options.pendingAddressDetails
            ? `${options.pendingAddressDetails.address} ${options.pendingAddressDetails.houseNumber}, ${options.pendingAddressDetails.postalCode} ${options.pendingAddressDetails.city}, ${options.pendingAddressDetails.country}`
            : '';
        
        this.pendingFullAddress(pendingFullAddress);
        
        const popup = options.type === EmployeeProfileMutationPopupModelType.Address
            ? 'employee-address-mutation-popup'
            : 'employee-profile-mutation-popup';

        openPopup(popup);
    }

    async submit() {
        let data: AddEmployeeProfileMutationRequest = {
            employeeId: this.employeeId,
            mutationStartDate: this.mutationStartDate().format('YYYY-MM-DD'),
        };
        
        switch(this.type()) {
            case EmployeeProfileMutationPopupModelType.Iban:
                data.values = {
                    [EmployeeProfileMutationPopupModelType.Iban]: this.iban()
                };
                break;
            case EmployeeProfileMutationPopupModelType.PayrollTaxDeduction:
                data.values = {
                    [EmployeeProfileMutationPopupModelType.PayrollTaxDeduction]: (!this.payrollTaxDeductionEnabled()).toString()
                };
                break;
            case EmployeeProfileMutationPopupModelType.Address:
                data.values = {
                    [EmployeeProfileMutationPopupModelType.Address]: this.address(),
                    [EmployeeProfileMutationPopupModelType.HouseNumber]: this.houseNumber(),
                    [EmployeeProfileMutationPopupModelType.PostalCode]: this.postalCode(),
                    [EmployeeProfileMutationPopupModelType.City]: this.city(),
                    [EmployeeProfileMutationPopupModelType.Country]: this.country()
                };
                break;
        }
        
        this.saving(true);

        const response = await fetch(this.submitUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });

        this.saving(false);

        if (response.ok) {
            this.validationErrors([]);
            this.closePopup();
            successToast(`De wijzigingen zijn opgeslagen en worden toegepast per ${this.mutationStartDate().format('YYYY-MM-DD')}`);

            if (this.onSuccess) {
                this.onSuccess();
            }
        } else if (response.errors && response.errors.length > 0) {
            this.validationErrors(response.errors);
            errorToast('Er ging iets mis tijdens het opslaan');
        }
    }

    closePopup() {
        closePopups();
    }
}
